// src/utils/pricingReport.ts
//
// CSV builder for the admin catalog pricing report (AdminPricingReport) — one
// row per product × size bucket with current price, blank cost, margin, and
// the recommended price at the target margin. Pure string builders (no DOM)
// except triggerDownload, mirroring economicReport.ts.

import { AdminPricingReport } from "../api/admin";

/** cents → "1234.56" (plain decimal dollars for CSV cells); "" when null. */
const dollars = (cents: number | null): string =>
  cents == null ? "" : (cents / 100).toFixed(2);

const pct = (p: number | null): string => (p == null ? "" : String(p));

/** RFC-4180 CSV cell: quote when it contains a comma, quote, or newline. */
const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** One row per product × size, worst-margin products first (server order). */
export const buildPricingCsv = (data: AdminPricingReport): string => {
  const rows: string[] = [];
  rows.push(
    [
      "product",
      "size",
      "variations",
      "current_price",
      "blank_cost",
      "current_margin_pct",
      "minimum_price",
      "recommended_price",
      "recommended_margin_pct",
      "change",
      "action",
      // Advisory placement columns — see AdminPricingPlacements. Not reflected
      // in blank_cost, margin, or recommended_price.
      "placements",
      "extra_placements",
      "placement_addon_cost",
      "placement_price_impact",
      "placement_detail",
    ].join(","),
  );

  for (const p of data.products) {
    for (const s of p.sizes) {
      const pl = p.placements;
      rows.push(
        [
          p.name,
          s.size,
          s.variations,
          dollars(s.priceCents),
          dollars(s.costCents),
          pct(s.marginPct),
          dollars(s.minimumCents),
          dollars(s.recommendedCents),
          pct(s.recommendedMarginPct),
          s.recommendedCents == null
            ? ""
            : dollars(s.recommendedCents - s.priceCents),
          p.action,
          pl ? pl.count : "",
          pl ? pl.extraCount : "",
          pl && pl.extraCount > 0 ? dollars(pl.addOnCents) : "",
          pl && pl.extraCount > 0 ? dollars(pl.priceImpactCents) : "",
          pl
            ? pl.list
                .map(
                  (d) =>
                    `${d.placement}${d.technique ? ` (${d.technique})` : ""}`,
                )
                .join(" + ")
            : "",
        ]
          .map(csvCell)
          .join(","),
      );
    }
  }

  return rows.join("\r\n");
};

const triggerDownload = (
  filename: string,
  content: string,
  mime: string,
): void => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadPricingCsv = (data: AdminPricingReport): void =>
  triggerDownload(
    `pricing-report_${data.targetPct}pct_${data.generatedAt.slice(0, 10)}.csv`,
    buildPricingCsv(data),
    "text/csv;charset=utf-8",
  );
